import React, { useState, useRef, useEffect } from "react";
import { BASE_URL as API_URL } from '../services/api';

export default function AIQueryChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendQuestion = async () => {
    if (!input.trim() || loading) return;

    const question = input.trim();
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setInput("");
    setLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_URL}/ai-query/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ question }),
      });

      const data = await res.json();

      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          text: data.answer || "Couldn't get an answer.",
          sql: data.sqlUsed || data.queriesUsed,
          success: data.success,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: "Something went wrong. Please try again.", success: false },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#f8fafc]">
      {/* Chat messages area */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
        {messages.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center my-auto min-h-[300px]">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4 shadow-sm">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="font-bold text-gray-800 text-base mb-1">Hi, I'm Luna!</h4>
            <p className="text-xs text-gray-500 max-w-[280px] mb-6">
              I can help you query database records, generate reports, and analyze CRM data.
            </p>
            <div className="w-full flex flex-col gap-2 max-w-[300px]">
              <button 
                onClick={() => setInput("How many orders today?")}
                className="text-left text-xs bg-white hover:bg-blue-50/50 border border-gray-100 hover:border-blue-200 p-3 rounded-xl transition-all text-gray-600 font-medium shadow-sm flex items-center justify-between group active:scale-95"
              >
                <span>"How many orders today?"</span>
                <span className="text-gray-400 group-hover:text-blue-600 transition-colors">→</span>
              </button>
              <button 
                onClick={() => setInput("Generate this month's MIS report")}
                className="text-left text-xs bg-white hover:bg-blue-50/50 border border-gray-100 hover:border-blue-200 p-3 rounded-xl transition-all text-gray-600 font-medium shadow-sm flex items-center justify-between group active:scale-95"
              >
                <span>"Generate this month's MIS report"</span>
                <span className="text-gray-400 group-hover:text-blue-600 transition-colors">→</span>
              </button>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex max-w-[85%] ${
              msg.role === "user" ? "self-end" : "self-start"
            }`}
          >
            <div
              className={`text-sm px-4 py-2.5 shadow-sm transition-all ${
                msg.role === "user"
                  ? "bg-blue-600 text-white rounded-2xl rounded-tr-sm"
                  : "bg-white border border-gray-100 text-gray-800 rounded-2xl rounded-tl-sm leading-relaxed"
              }`}
            >
              {msg.role === "ai" && msg.success === false && (
                <div className="text-xs text-red-600 font-semibold mb-1">
                  ⚠️ Error
                </div>
              )}
              <div className="whitespace-pre-wrap">{msg.text}</div>

              {msg.role === "ai" && msg.sql && (
                <details className="mt-2.5 text-xs border-t border-gray-100 pt-2 cursor-pointer">
                  <summary className="text-blue-600 hover:text-blue-800 font-semibold select-none flex items-center gap-1">
                    <span>View SQL Query</span>
                  </summary>
                  <pre className="mt-2 p-2.5 bg-gray-50 rounded-lg border border-gray-150 overflow-x-auto text-[11px] font-mono text-gray-600 whitespace-pre-wrap leading-tight">
                    {msg.sql}
                  </pre>
                </details>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="self-start max-w-[85%] bg-white border border-gray-100 text-gray-500 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input row */}
      <div className="p-3 bg-white border-t border-gray-100 flex gap-2 items-center flex-shrink-0">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your question..."
          disabled={loading}
          className="flex-1 px-4 py-2.5 rounded-full border border-gray-250 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 transition-all bg-gray-50/50"
        />
        <button
          onClick={sendQuestion}
          disabled={loading || !input.trim()}
          className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:bg-gray-250 disabled:text-gray-400 disabled:cursor-not-allowed transition-all shadow active:scale-95 flex-shrink-0"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4 rotate-45 transform translate-x-[-1px]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>

      <p className="text-[10px] text-gray-400 text-center py-2 px-3 border-t border-gray-50 bg-white flex-shrink-0">
        AI-generated answer — please verify before using in critical reports.
      </p>
    </div>
  );
}